type AnyFunction = (...args: any[]) => any

/**
 * This is a helper type to make the function declaration below easier to comprehend.
 *
 * It says that any function that is declared as DeferredFunctionCopy<T> will take the
 * same parameters as T and return the same type as T, but deferred (wrapped in a promise).
 */ 
type DeferredFunctionCopy<F extends AnyFunction> = (...args: Parameters<F>) => Promise<Awaited<ReturnType<F>>>

export function debounce<F extends AnyFunction>(delayedFunc: F, delay = 500): DeferredFunctionCopy<F> {
  let timer: NodeJS.Timeout

  return (...args: Parameters<F>) => {
    let resolve: (value: ReturnType<F>) => void
    let reject: (reason?: unknown) => void

    const promise = new Promise<Awaited<ReturnType<F>>>((internalResolve, internalReject) => {
      resolve = internalResolve
      reject = internalReject
    });

    clearTimeout(timer)
    timer = setTimeout(() => {
      try {
        resolve(delayedFunc(...args))
      } catch (ex) {
        reject(ex)
      }
    }, delay)

    return promise
  }
}
